import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_API_BASE_URL;

const BulkUpdateAddress = () => {


  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);

  const [processing, setProcessing] = useState(false);

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");



  // --------------------------------------------------
  // FILE SELECT
  // --------------------------------------------------
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    setError("");
    setResult(null);
    setRows([]);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const extension = selectedFile.name
      .split(".")
      .pop()
      .toLowerCase();

    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setError("Please select an Excel (.xlsx/.xls) or CSV file.");
      e.target.value = "";
      setFile(null);
      return;
    }

    setFile(selectedFile);

    readExcelFile(selectedFile);
  };

  // --------------------------------------------------
  // READ EXCEL / CSV
  // --------------------------------------------------
  const readExcelFile = (selectedFile) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);

        const workbook = XLSX.read(data, {
          type: "array",
        });

        const firstSheetName = workbook.SheetNames[0];

        if (!firstSheetName) {
          setError("Excel file does not contain any sheet.");
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
        });

        if (!jsonData.length) {
          setError("The selected file is empty.");
          return;
        }

        // ----------------------------------------------
        // NORMALIZE ONLY REQUIRED FIELDS
        // ----------------------------------------------
        const normalizedRows = jsonData.map((item, index) => {
          const admissionId = String(
            item.admission_id ??
              item.admissionId ??
              item["Admission ID"] ??
              item["Admission Id"] ??
              item["Admission No"] ??
              item["Admission Number"] ??
              ""
          ).trim();

          const address = String(
            item.address ??
              item.Address ??
              ""
          ).trim();

          return {
            rowNumber: index + 2,
            admission_id: admissionId,
            address: address,
          };
        });

        setRows(normalizedRows);
      } catch (err) {
        console.error("Excel read error:", err);
        setError("Unable to read the selected file.");
        setRows([]);
      }
    };

    reader.onerror = () => {
      setError("Unable to read the selected file.");
      setRows([]);
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  // --------------------------------------------------
  // REMOVE FILE
  // --------------------------------------------------
  const handleRemoveFile = () => {
    setFile(null);
    setRows([]);
    setResult(null);
    setError("");

    const input = document.getElementById("addressExcelFile");

    if (input) {
      input.value = "";
    }
  };

  // --------------------------------------------------
  // VALIDATE ROWS
  // --------------------------------------------------
const validRows = rows.filter(
  (row) => row.admission_id
);

const invalidRows = rows.filter(
  (row) => !row.admission_id
);

  // --------------------------------------------------
  // BULK UPDATE ADDRESS
  // --------------------------------------------------
  const handleBulkUpdate = async () => {
    setError("");
    setResult(null);



    if (!file) {
      setError("Please select an Excel/CSV file.");
      return;
    }

    if (!rows.length) {
      setError("No records found in the selected file.");
      return;
    }

if (invalidRows.length > 0) {
  setError(
    `${invalidRows.length} row(s) have missing Admission ID. Please fix the Excel file before updating.`
  );
  return;
}

    try {
      setProcessing(true);

const response = await axios.put(
  `${API}/api/students/bulkUpdateAddress`,
  {
    rows: validRows.map((row) => ({
      admission_id: row.admission_id,
      address: row.address,
    })),
  }
);

      setResult(response.data);

      if (response.data?.success) {
        // Keep preview visible after successful update.
        // File is not automatically removed.
      }
    } catch (err) {
      console.error("Bulk address update error:", err);

      setError(
        err.response?.data?.message ||
          "Bulk address update failed."
      );
    } finally {
      setProcessing(false);
    }
  };

  // --------------------------------------------------
  // DOWNLOAD SAMPLE EXCEL
  // --------------------------------------------------
  const downloadSample = () => {
    const sampleData = [
      {
        admission_id: "B92",
        address: "VPO Nattar, Sirsa",
      },
      {
        admission_id: "B93",
        address: "Near Bus Stand, Sirsa",
      },
      {
        admission_id: "B94",
        address: "Ward No 4, Sirsa",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);

    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 45 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Student Addresses"
    );

    XLSX.writeFile(
      workbook,
      "Bulk_Address_Update_Sample.xlsx"
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">

        {/* ------------------------------------------------ */}
        {/* HEADER */}
        {/* ------------------------------------------------ */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Bulk Update Student Address
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Update student addresses using Admission ID.
            Only the address field will be updated.
          </p>
        </div>

        {/* ------------------------------------------------ */}
        {/* MAIN CARD */}
        {/* ------------------------------------------------ */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">

          {/* ---------------------------------------------- */}
          {/* CARD HEADER */}
          {/* ---------------------------------------------- */}
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Upload Address File
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Select the session and upload your Excel/CSV file.
            </p>
          </div>

          {/* ---------------------------------------------- */}
          {/* FORM */}
          {/* ---------------------------------------------- */}
          <div className="p-5">

            <div className="grid grid-cols-1 gap-5">



              {/* FILE */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Excel / CSV File
                </label>

                <input
                  id="addressExcelFile"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  disabled={processing}
                  className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-white text-sm text-gray-600 file:mr-4 file:border-0 file:bg-gray-100 file:px-4 file:py-2.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                />
              </div>

            </div>

            {/* -------------------------------------------- */}
            {/* FILE INFO */}
            {/* -------------------------------------------- */}
            {file && (
              <div className="mt-5 flex flex-col justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center">

                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    {file.name}
                  </p>

                  <p className="mt-1 text-xs text-blue-600">
                    {rows.length} record(s) found
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleRemoveFile}
                  disabled={processing}
                  className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Remove File
                </button>
              </div>
            )}

            {/* -------------------------------------------- */}
            {/* FORMAT INFORMATION */}
            {/* -------------------------------------------- */}
            <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">

              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">

                <div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    Excel Format
                  </h3>

                  <p className="mt-1 text-xs text-gray-500">
                    Your file must contain only Admission ID and Address.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={downloadSample}
                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                >
                  Download Sample
                </button>

              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2 font-semibold text-gray-700">
                        admission_id
                      </th>

                      <th className="px-3 py-2 font-semibold text-gray-700">
                        address
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-gray-600">
                        B92
                      </td>

                      <td className="px-3 py-2 text-gray-600">
                        VPO Nattar, Sirsa
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>

            {/* -------------------------------------------- */}
            {/* ERROR */}
            {/* -------------------------------------------- */}
            {error && (
              <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* -------------------------------------------- */}
            {/* UPDATE BUTTON */}
            {/* -------------------------------------------- */}
            <div className="mt-6 flex justify-end">

              <button
                type="button"
                onClick={handleBulkUpdate}
disabled={
  processing ||
  !file ||
  !rows.length
}
                className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {processing
                  ? "Updating Addresses..."
                  : "Update Addresses"}
              </button>

            </div>
          </div>
        </div>

        {/* ------------------------------------------------ */}
        {/* PREVIEW */}
        {/* ------------------------------------------------ */}
        {rows.length > 0 && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm">

            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">

                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    File Preview
                  </h2>

                  <p className="text-sm text-gray-500">
                    Review the records before updating.
                  </p>
                </div>

                <div className="flex gap-3 text-xs">
                  <span className="rounded-full bg-green-100 px-3 py-1 font-medium text-green-700">
                    Valid: {validRows.length}
                  </span>

                  <span className="rounded-full bg-red-100 px-3 py-1 font-medium text-red-700">
                    Invalid: {invalidRows.length}
                  </span>
                </div>

              </div>
            </div>

            <div className="max-h-[500px] overflow-auto">

              <table className="min-w-full text-left text-sm">

                <thead className="sticky top-0 bg-gray-100">
                  <tr>
                    <th className="border-b px-4 py-3 font-semibold text-gray-700">
                      #
                    </th>

                    <th className="border-b px-4 py-3 font-semibold text-gray-700">
                      Admission ID
                    </th>

                    <th className="border-b px-4 py-3 font-semibold text-gray-700">
                      Address
                    </th>

                    <th className="border-b px-4 py-3 font-semibold text-gray-700">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => {
                    const valid =
                      row.admission_id && row.address;

                    return (
                      <tr
                        key={`${row.rowNumber}-${index}`}
                        className="hover:bg-gray-50"
                      >

                        <td className="border-b px-4 py-3 text-gray-500">
                          {index + 1}
                        </td>

                        <td className="border-b px-4 py-3 font-medium text-gray-800">
                          {row.admission_id || "-"}
                        </td>

                        <td className="border-b px-4 py-3 text-gray-700">
                          {row.address || "-"}
                        </td>

                        <td className="border-b px-4 py-3">
                          {valid ? (
                            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                              Ready
                            </span>
                          ) : (
                            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                              Invalid
                            </span>
                          )}
                        </td>

                      </tr>
                    );
                  })}
                </tbody>

              </table>
            </div>
          </div>
        )}

        {/* ------------------------------------------------ */}
        {/* RESULT */}
        {/* ------------------------------------------------ */}
        {result && (
          <div className="mt-6 rounded-xl border border-green-200 bg-white shadow-sm">

            <div className="border-b border-green-200 bg-green-50 px-5 py-4">
              <h2 className="text-lg font-semibold text-green-800">
                Bulk Update Completed
              </h2>

              <p className="mt-1 text-sm text-green-700">
                Address update process has been completed.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs font-medium text-gray-500">
                  Total Rows
                </p>

                <p className="mt-1 text-2xl font-bold text-gray-800">
                  {result.totalRows || 0}
                </p>
              </div>

              <div className="rounded-lg bg-green-50 p-4">
                <p className="text-xs font-medium text-green-600">
                  Updated
                </p>

                <p className="mt-1 text-2xl font-bold text-green-700">
                  {result.updatedCount || 0}
                </p>
              </div>

              <div className="rounded-lg bg-yellow-50 p-4">
                <p className="text-xs font-medium text-yellow-600">
                  Not Found
                </p>

                <p className="mt-1 text-2xl font-bold text-yellow-700">
                  {result.notFoundCount || 0}
                </p>
              </div>

              <div className="rounded-lg bg-red-50 p-4">
                <p className="text-xs font-medium text-red-600">
                  Skipped
                </p>

                <p className="mt-1 text-2xl font-bold text-red-700">
                  {result.skippedCount || 0}
                </p>
              </div>

            </div>

            {/* NOT FOUND */}
            {result.notFound?.length > 0 && (
              <div className="border-t border-gray-200 p-5">

                <h3 className="mb-3 text-sm font-semibold text-gray-800">
                  Students Not Found
                </h3>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">

                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 font-semibold">
                          Admission ID
                        </th>

                        <th className="px-4 py-2 font-semibold">
                          Reason
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {result.notFound.map((item, index) => (
                        <tr key={index}>
                          <td className="border-b px-4 py-2">
                            {item.admission_id || "-"}
                          </td>

                          <td className="border-b px-4 py-2 text-red-600">
                            {item.reason || "Student not found"}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>

              </div>
            )}

            {/* SKIPPED */}
            {result.skipped?.length > 0 && (
              <div className="border-t border-gray-200 p-5">

                <h3 className="mb-3 text-sm font-semibold text-gray-800">
                  Skipped Rows
                </h3>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">

                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 font-semibold">
                          Admission ID
                        </th>

                        <th className="px-4 py-2 font-semibold">
                          Reason
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {result.skipped.map((item, index) => (
                        <tr key={index}>
                          <td className="border-b px-4 py-2">
                            {item.admission_id || "-"}
                          </td>

                          <td className="border-b px-4 py-2 text-red-600">
                            {item.reason || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>

              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default BulkUpdateAddress;